/**
 * The task-first landing recipe (#1466 / ADR F-30).
 *
 * It owns order and disclosure, not data: attention first, then the user's
 * chosen/recent context, then secondary information. Hosts adapt governed rows
 * into this finite shape and keep every act in its owning module.
 */
import type { ReactNode } from 'react'
import { Button, EmptyState, Spinner } from '../controls'
import { PageShell, type PageWidth } from '../shell/PageShell'
import { PageSection } from '../shell/PageSection'
import s from './WorkHome.module.css'

interface WorkHomeItemBase {
  id: string
  title: ReactNode
  reason?: ReactNode
  detail?: ReactNode
  meta?: ReactNode
  status?: ReactNode
  /** The owning domain's governed act; WorkHome never invents one. */
  action?: ReactNode
  featured?: boolean
}

export type WorkHomeItem = WorkHomeItemBase & (
  | { onOpen: () => void; openLabel: string }
  | { onOpen?: undefined; openLabel?: never }
)

export type WorkHomeFeed =
  | { state: 'loading'; label: ReactNode }
  | { state: 'error'; message: ReactNode; action?: ReactNode }
  | { state: 'ready'; items: readonly WorkHomeItem[]; empty: ReactNode }

export interface WorkHomeProps {
  eyebrow?: ReactNode
  title: ReactNode
  summary?: ReactNode
  status?: ReactNode
  primaryAction?: ReactNode
  secondaryActions?: ReactNode
  appearance?: 'default' | 'feature'
  highlights?: ReactNode
  width?: PageWidth
  testId?: string
  attentionLabel: ReactNode
  attentionSummary?: ReactNode
  attention: WorkHomeFeed
  pinnedLabel?: ReactNode
  pinned?: readonly WorkHomeItem[]
  recentLabel?: ReactNode
  recent?: readonly WorkHomeItem[]
  secondaryLabel?: ReactNode
  secondary?: ReactNode
}

export function WorkItems({ items, compact = false }: {
  items: readonly WorkHomeItem[]
  compact?: boolean
}) {
  return (
    <ul className={compact ? s.compactItems : s.items}>
      {items.map((item) => (
        <li key={item.id}>
          <article className={s.item} data-featured={item.featured || undefined}>
            <div className={s.itemHead}>
              <h3 className={s.itemTitle}>{item.title}</h3>
              {item.status != null && <div className={s.itemStatus}>{item.status}</div>}
            </div>
            {item.reason != null && <div className={s.reason}>{item.reason}</div>}
            {item.detail != null && <div className={s.detail}>{item.detail}</div>}
            {(item.meta != null || item.onOpen || item.action != null) && (
              <div className={s.itemFoot}>
                {item.meta != null && <div className={s.meta}>{item.meta}</div>}
                <div className={s.itemActions}>
                  {item.onOpen && (
                    <Button size="sm" variant={item.action != null ? 'ghost' : 'primary'} onClick={item.onOpen}>
                      {item.openLabel}
                    </Button>
                  )}
                  {item.action}
                </div>
              </div>
            )}
          </article>
        </li>
      ))}
    </ul>
  )
}

/**
 * The governed work-feed body without a page recipe.
 *
 * `WorkHome` remains the task-first route recipe. Landing pages with a richer
 * editorial composition can reuse the exact same loading/error/empty/item
 * contract without nesting that whole recipe or copying how work is drawn.
 */
export function WorkFeed({ feed }: { feed: WorkHomeFeed }) {
  if (feed.state === 'loading') {
    return <div className={s.state} aria-live="polite"><Spinner /><span>{feed.label}</span></div>
  }
  if (feed.state === 'error') {
    return (
      <div className={s.error} role="alert">
        <span>{feed.message}</span>
        {feed.action}
      </div>
    )
  }
  if (feed.items.length === 0) return <EmptyState>{feed.empty}</EmptyState>
  return <WorkItems items={feed.items} />
}

export function WorkHome({
  eyebrow,
  title,
  summary,
  status,
  primaryAction,
  secondaryActions,
  appearance,
  highlights,
  width = 'wide',
  testId,
  attentionLabel,
  attentionSummary,
  attention,
  pinnedLabel,
  pinned = [],
  recentLabel,
  recent = [],
  secondaryLabel,
  secondary,
}: WorkHomeProps) {
  return (
    <PageShell
      eyebrow={eyebrow}
      title={title}
      summary={summary}
      status={status}
      primaryAction={primaryAction}
      secondaryActions={secondaryActions}
      appearance={appearance}
      highlights={highlights}
      width={width}
      testId={testId}
    >
      <PageSection title={attentionLabel} summary={attentionSummary}>
        <WorkFeed feed={attention} />
      </PageSection>

      {pinned.length > 0 && pinnedLabel != null && (
        <PageSection title={pinnedLabel}>
          <WorkItems items={pinned} compact />
        </PageSection>
      )}

      {recent.length > 0 && recentLabel != null && (
        <PageSection title={recentLabel}>
          <WorkItems items={recent} compact />
        </PageSection>
      )}

      {secondary != null && (
        secondaryLabel != null
          ? <PageSection title={secondaryLabel}><div className={s.secondaryBody}>{secondary}</div></PageSection>
          : <div className={s.secondary}>{secondary}</div>
      )}
    </PageShell>
  )
}
